import * as THREE from 'three';
import { BUILD } from '../../config';
import { S, TRI_R, TRI_EDGES } from './grid';

// The piece catalog: a static, typed registry describing EVERY buildable. Each def carries its
// footprint family, structural role, the host/guest anchors it exposes, the occupancy slots it
// reserves, its support behavior, a local Box3 for the fit pass, and its allowed rotation set.
// All build-tuning constants are LOCAL to this module (config.ts BUILD is read but never extended).
// This file is standalone: it does NOT import from building.ts.

export type PieceId =
  | 'foundation_square' | 'foundation_triangle'
  | 'ceiling_square' | 'ceiling_triangle'
  | 'wall' | 'half_wall' | 'doorway' | 'double_doorway' | 'window' | 'door'
  | 'stairs' | 'pillar';

export type FootprintFamily = 'square' | 'triangle' | 'none';

export type StructuralRole =
  | 'foundation' | 'triangleFoundation'
  | 'wall' | 'doorway' | 'window' | 'halfWall'
  | 'floor' | 'triangleFloor'   // ceiling_square / ceiling_triangle map here
  | 'stairs' | 'door' | 'pillar';

export type AnchorFamily =
  | 'ground'                    // terrain-grounded foundation base (special rule path)
  | 'ground_edge_square' | 'ground_edge_triangle'
  | 'wall_base'                 // bottom of a vertical edge piece
  | 'wall_top_square' | 'wall_top_triangle'   // top-of-stack cap level for ceilings
  | 'floor_edge_square' | 'floor_edge_triangle'
  | 'door_host'                 // doorway opening that accepts a door leaf
  | 'floor_top';                // floor surface that hosts cupboard/stairs base

export type SlotKind =
  | 'cell_square' | 'cell_triangle'   // footprint cell at a height band
  | 'edge_square' | 'edge_triangle'   // a wall stack on one perimeter edge at a support height
  | 'top_square' | 'top_triangle'     // ceiling cap on a support
  | 'door_opening'                    // the doorway leaf slot
  | 'stair_volume'                    // the climb cell a staircase fills (one stair per cell + band)
  | 'pillar_corner';                  // a pillar standing on one footprint corner at a height band

export type SupportBehavior =
  | { kind: 'grounded'; base: number }            // foundation grounded on terrain -> 1.0
  | { kind: 'adjacentFoundation'; loss: number }  // edge-extended foundation -> neighbor - 0.05
  | { kind: 'wallUp'; loss: number }              // wall transmits up, loss 0.02
  | { kind: 'floorConsume'; loss: number }        // ceiling/floor consumes 0.10
  | { kind: 'roofConsume'; loss: number }         // roof slightly more than floor
  | { kind: 'pillar'; loss: number }              // very low loss
  | { kind: 'parasite' };                         // door/cupboard/stairs: inherit host, never crumble alone

export type RuleId =
  | 'foundationTerrainFit'   // §B legs-reach + not-buried
  | 'footprintNoOverlap'     // SAT against same-height footprints
  | 'wallEdgeSupported'      // edge has a foundation/floor under it
  | 'ceilingNoOverlap'       // updateCeiling overlap test
  | 'doorwayOnly'            // door attaches to an empty doorway
  | 'floorBelowPlayer';      // cupboard/stairs onto the storey under playerY

export interface BuildAnchorDef {
  id: string;
  family: AnchorFamily;
  role: 'host' | 'guest';
  localPosition: [number, number, number];
  localQuaternion: [number, number, number, number]; // x,y,z,w
  accepts: AnchorFamily[];      // for guests: which host families it bonds to
  maxSnapDistance: number;      // build-local constant (NOT config.ts)
  maxAngleDegrees: number;
}

export interface OccupancySlotDef {
  kind: SlotKind;
  localKey: string;             // e.g. 'cell', 'edge_n', 'top', 'door' -- resolved to a world key on placement
  exclusiveWith: SlotKind[];    // kinds that may not co-occupy the same resolved key
}

export interface BuildPieceDef {
  id: PieceId;
  footprintFamily: FootprintFamily;
  structuralRole: StructuralRole;
  anchors: BuildAnchorDef[];
  occupancy: OccupancySlotDef[];
  support: SupportBehavior;
  boundsLocal: THREE.Box3;       // for the Box3 fit pass
  allowedRotationsDeg: number[]; // square: derived from 90; triangle: 120; free pieces: [] (continuous notch)
  rules: RuleId[];               // ordered piece-specific rules
}

// --- build-local constants (NOT config.ts -- see §F) ---

export const ANCHOR_MAX_SNAP = S * BUILD.socketReach; // reuse the existing socket reach feel
export const ANCHOR_MAX_ANGLE = 25; // degrees, generous for ghost following

// Stability losses (best-parent value minus this piece's loss; grounded seeds at 1.0).
export const SUPPORT = {
  grounded: 1.0,
  adjFound: 0.05,
  wallUp: 0.02,
  floor: 0.1,
  roof: 0.14, // slightly more than a floor (reserved for the future roof piece)
  pillar: 0.01,
  // A floor/ceiling RESTING on a vertical support beneath it (wall/pillar/foundation) is carried
  // straight down -> tiny loss. One only HANGING off a neighbouring floor/ceiling is a horizontal
  // cantilever and decays fast: at 0.5, an unsupported roof reaches ~1 tile past its walls before it
  // must rest on a pillar. (This is what stops floating ceiling-cross platforms.)
  floorOnSupport: 0.02,
  cantilever: 0.5,
} as const;

export const CRUMBLE_SECONDS = 1.0; // 0.8–1.2 range
export const CRUMBLE_SINK = 1.4;    // metres dropped while fading

// Derived heights used to place anchors in each piece's local mesh frame.
const HALF = BUILD.wallHeight / 2;
const FOUND_TOP_Y = BUILD.foundDepth / 2;  // foundation walkable top face, local Y
const FOUND_BASE_Y = -BUILD.foundDepth / 2; // foundation underside, local Y
const IDENTITY_Q: [number, number, number, number] = [0, 0, 0, 1];

// Full set of 90deg rotations (square) and 120deg rotations (triangle).
const SQUARE_ROTATIONS = [0, 90, 180, 270];
const TRI_ROTATIONS = [0, 120, 240];

// Helpers to spell out the per-edge anchors of a footprint piece. Each square edge mid is at a
// cardinal (±S/2); each triangle edge mid comes from buildTriEdges (used for occupancy keys only --
// the anchor positions below mirror the same midpoints).
function squareEdgeGuests(family: AnchorFamily, topY: number): BuildAnchorDef[] {
  const mids: Array<[number, number, number, number, string]> = [
    [S / 2, 0, 1, 0, 'e'],
    [-S / 2, 0, -1, 0, 'w'],
    [0, S / 2, 0, 1, 'n'],
    [0, -S / 2, 0, -1, 's'],
  ];
  return mids.map(([mx, mz, nx, nz, tag]) => ({
    id: `edge_guest_${tag}`,
    family,
    role: 'guest' as const,
    localPosition: [mx, topY, mz] as [number, number, number],
    localQuaternion: outwardQuat(nx, nz),
    accepts: [family],
    maxSnapDistance: ANCHOR_MAX_SNAP,
    maxAngleDegrees: ANCHOR_MAX_ANGLE,
  }));
}

function squareEdgeHosts(family: AnchorFamily, topY: number): BuildAnchorDef[] {
  const mids: Array<[number, number, number, number, string]> = [
    [S / 2, 0, 1, 0, 'e'],
    [-S / 2, 0, -1, 0, 'w'],
    [0, S / 2, 0, 1, 'n'],
    [0, -S / 2, 0, -1, 's'],
  ];
  return mids.map(([mx, mz, nx, nz, tag]) => ({
    id: `edge_host_${tag}`,
    family,
    role: 'host' as const,
    localPosition: [mx, topY, mz] as [number, number, number],
    localQuaternion: outwardQuat(nx, nz),
    accepts: [family],
    maxSnapDistance: ANCHOR_MAX_SNAP,
    maxAngleDegrees: ANCHOR_MAX_ANGLE,
  }));
}

function triEdgeGuests(family: AnchorFamily, topY: number): BuildAnchorDef[] {
  return TRI_EDGES.map((e, i) => ({
    id: `edge_guest_${i}`,
    family,
    role: 'guest' as const,
    localPosition: [e.mx, topY, e.mz] as [number, number, number],
    localQuaternion: outwardQuat(e.nx, e.nz),
    accepts: [family],
    maxSnapDistance: ANCHOR_MAX_SNAP,
    maxAngleDegrees: ANCHOR_MAX_ANGLE,
  }));
}

function triEdgeHosts(family: AnchorFamily, topY: number): BuildAnchorDef[] {
  return TRI_EDGES.map((e, i) => ({
    id: `edge_host_${i}`,
    family,
    role: 'host' as const,
    localPosition: [e.mx, topY, e.mz] as [number, number, number],
    localQuaternion: outwardQuat(e.nx, e.nz),
    accepts: [family],
    maxSnapDistance: ANCHOR_MAX_SNAP,
    maxAngleDegrees: ANCHOR_MAX_ANGLE,
  }));
}

// A quaternion whose +Z (or the chosen forward) points along the outward edge normal (nx,nz) in
// the XZ plane. Used only as orientation metadata for the anchor (the solver derives yaw itself).
function outwardQuat(nx: number, nz: number): [number, number, number, number] {
  const yaw = Math.atan2(nx, nz);
  const q = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), yaw);
  return [q.x, q.y, q.z, q.w];
}

function box3(hx: number, loY: number, hiY: number, hz: number): THREE.Box3 {
  return new THREE.Box3(new THREE.Vector3(-hx, loY, -hz), new THREE.Vector3(hx, hiY, hz));
}

// Square edge occupancy slots (one wall stack per perimeter edge). exclusiveWith [] because each
// added wall raises the support top and so lands in a NEW height band (stacking up is allowed).
const SQUARE_EDGE_SLOTS: OccupancySlotDef[] = ['edge_n', 'edge_e', 'edge_s', 'edge_w'].map((k) => ({
  kind: 'edge_square' as const, localKey: k, exclusiveWith: [],
}));
const TRI_EDGE_SLOTS: OccupancySlotDef[] = ['edge_0', 'edge_1', 'edge_2'].map((k) => ({
  kind: 'edge_triangle' as const, localKey: k, exclusiveWith: [],
}));

// ============================ per-piece definitions ============================

const foundation_square: BuildPieceDef = {
  id: 'foundation_square',
  footprintFamily: 'square',
  structuralRole: 'foundation',
  anchors: [
    { id: 'ground', family: 'ground', role: 'guest', localPosition: [0, FOUND_BASE_Y, 0], localQuaternion: IDENTITY_Q, accepts: ['ground'], maxSnapDistance: ANCHOR_MAX_SNAP, maxAngleDegrees: ANCHOR_MAX_ANGLE },
    ...squareEdgeGuests('ground_edge_square', FOUND_TOP_Y),
    ...squareEdgeHosts('ground_edge_square', FOUND_TOP_Y),
  ],
  occupancy: [
    { kind: 'cell_square', localKey: 'cell', exclusiveWith: ['cell_square', 'cell_triangle'] },
    ...SQUARE_EDGE_SLOTS,
  ],
  support: { kind: 'grounded', base: SUPPORT.grounded },
  boundsLocal: box3(S / 2, FOUND_BASE_Y, FOUND_TOP_Y, S / 2),
  allowedRotationsDeg: SQUARE_ROTATIONS,
  rules: ['foundationTerrainFit', 'footprintNoOverlap'],
};

const foundation_triangle: BuildPieceDef = {
  id: 'foundation_triangle',
  footprintFamily: 'triangle',
  structuralRole: 'triangleFoundation',
  anchors: [
    { id: 'ground', family: 'ground', role: 'guest', localPosition: [0, FOUND_BASE_Y, 0], localQuaternion: IDENTITY_Q, accepts: ['ground'], maxSnapDistance: ANCHOR_MAX_SNAP, maxAngleDegrees: ANCHOR_MAX_ANGLE },
    ...triEdgeGuests('ground_edge_triangle', FOUND_TOP_Y),
    ...triEdgeHosts('ground_edge_triangle', FOUND_TOP_Y),
  ],
  occupancy: [
    { kind: 'cell_triangle', localKey: 'cell', exclusiveWith: ['cell_square', 'cell_triangle'] },
    ...TRI_EDGE_SLOTS,
  ],
  support: { kind: 'grounded', base: SUPPORT.grounded },
  boundsLocal: box3(TRI_R, FOUND_BASE_Y, FOUND_TOP_Y, TRI_R),
  allowedRotationsDeg: TRI_ROTATIONS,
  rules: ['foundationTerrainFit', 'footprintNoOverlap'],
};

// Walls (and half walls): the box is centered at its mid-height, so the base anchor sits at -h/2
// and the top (cap) anchor at +h/2.
function wallDef(id: 'wall' | 'half_wall' | 'doorway' | 'double_doorway' | 'window'): BuildPieceDef {
  const h = id === 'half_wall' ? HALF : BUILD.wallHeight;
  const baseGuest: BuildAnchorDef = {
    id: 'wall_base', family: 'wall_base', role: 'guest',
    localPosition: [0, -h / 2, 0], localQuaternion: IDENTITY_Q,
    accepts: ['ground_edge_square', 'ground_edge_triangle', 'floor_edge_square', 'floor_edge_triangle'],
    maxSnapDistance: ANCHOR_MAX_SNAP, maxAngleDegrees: ANCHOR_MAX_ANGLE,
  };
  const topHostSquare: BuildAnchorDef = {
    id: 'wall_top_square', family: 'wall_top_square', role: 'host',
    localPosition: [0, h / 2, 0], localQuaternion: IDENTITY_Q, accepts: ['wall_top_square'],
    maxSnapDistance: ANCHOR_MAX_SNAP, maxAngleDegrees: ANCHOR_MAX_ANGLE,
  };
  const topHostTri: BuildAnchorDef = {
    id: 'wall_top_triangle', family: 'wall_top_triangle', role: 'host',
    localPosition: [0, h / 2, 0], localQuaternion: IDENTITY_Q, accepts: ['wall_top_triangle'],
    maxSnapDistance: ANCHOR_MAX_SNAP, maxAngleDegrees: ANCHOR_MAX_ANGLE,
  };
  const anchors: BuildAnchorDef[] = [baseGuest, topHostSquare, topHostTri];
  const occupancy: OccupancySlotDef[] = [
    // Resolved to the stacked edge key (incl. height band) on placement. Exclusive against another
    // edge piece at the SAME band only -- stacking up lands in a fresh band.
    { kind: 'edge_square', localKey: 'edge', exclusiveWith: ['edge_square'] },
    { kind: 'edge_triangle', localKey: 'edge', exclusiveWith: ['edge_triangle'] },
  ];
  let role: StructuralRole = 'wall';
  if (id === 'half_wall') role = 'halfWall';
  else if (id === 'doorway' || id === 'double_doorway') role = 'doorway';
  else if (id === 'window') role = 'window';
  // A doorway hosts a door in its opening: a single leaf, or a leaf PAIR in the double doorway
  // (whose opening -- and so its hinge-side anchor -- is twice as wide). Both reserve ONE
  // door_opening slot: the pair of a double door acts as one unit, so one slot covers both leaves.
  if (id === 'doorway' || id === 'double_doorway') {
    const openW = BUILD.doorwayWidth * (id === 'double_doorway' ? 2 : 1);
    anchors.push({
      id: 'door_host', family: 'door_host', role: 'host',
      localPosition: [-openW / 2, 0, 0], localQuaternion: IDENTITY_Q, accepts: ['door_host'],
      maxSnapDistance: ANCHOR_MAX_SNAP, maxAngleDegrees: ANCHOR_MAX_ANGLE,
    });
    occupancy.push({ kind: 'door_opening', localKey: 'door', exclusiveWith: ['door_opening'] });
  }
  return {
    id,
    footprintFamily: 'none',
    structuralRole: role,
    anchors,
    occupancy,
    support: { kind: 'wallUp', loss: SUPPORT.wallUp },
    boundsLocal: box3(S / 2, -h / 2, h / 2, BUILD.wallThick / 2),
    allowedRotationsDeg: [], // free placement: continuous yaw, edge-snapped by the solver
    rules: ['wallEdgeSupported'],
  };
}

const wall = wallDef('wall');
const half_wall = wallDef('half_wall');
const doorway = wallDef('doorway');
const double_doorway = wallDef('double_doorway'); // a wider doorway -- crafted double doors snap into it
const window_ = wallDef('window');

// Ceilings: slab centered at -ceilingThick/2 below `top`. Two host paths -- edge-extend off any
// same-family foundation/ceiling edge, and cap a wall_top level. The edge guests sit at the slab's
// own height band (local Y 0, since the mesh center is the band reference) with outward normals.
function ceilingDef(id: 'ceiling_square' | 'ceiling_triangle'): BuildPieceDef {
  const square = id === 'ceiling_square';
  const edgeFamily: AnchorFamily = square ? 'floor_edge_square' : 'floor_edge_triangle';
  const groundFamily: AnchorFamily = square ? 'ground_edge_square' : 'ground_edge_triangle';
  const topGuestFamily: AnchorFamily = square ? 'wall_top_square' : 'wall_top_triangle';
  const edgeGuests = square
    ? squareEdgeGuests(edgeFamily, 0).map((a) => ({ ...a, accepts: [edgeFamily, groundFamily] }))
    : triEdgeGuests(edgeFamily, 0).map((a) => ({ ...a, accepts: [edgeFamily, groundFamily] }));
  const edgeHosts = square ? squareEdgeHosts(edgeFamily, 0) : triEdgeHosts(edgeFamily, 0);
  // The cap guest: bonds to a wall_top host. Sits at the slab underside center.
  const capGuest: BuildAnchorDef = {
    id: 'cap_guest', family: topGuestFamily, role: 'guest',
    localPosition: [0, -BUILD.ceilingThick / 2, 0], localQuaternion: IDENTITY_Q, accepts: [topGuestFamily],
    maxSnapDistance: ANCHOR_MAX_SNAP, maxAngleDegrees: ANCHOR_MAX_ANGLE,
  };
  const r = square ? S / 2 : TRI_R;
  const topKind: SlotKind = square ? 'top_square' : 'top_triangle';
  return {
    id,
    footprintFamily: square ? 'square' : 'triangle',
    structuralRole: square ? 'floor' : 'triangleFloor',
    anchors: [...edgeGuests, ...edgeHosts, capGuest],
    occupancy: [
      { kind: topKind, localKey: 'top', exclusiveWith: ['top_square', 'top_triangle'] },
    ],
    support: { kind: 'floorConsume', loss: SUPPORT.floor },
    boundsLocal: box3(r, -BUILD.ceilingThick, 0, r),
    allowedRotationsDeg: square ? SQUARE_ROTATIONS : TRI_ROTATIONS,
    rules: ['ceilingNoOverlap'],
  };
}

const ceiling_square = ceilingDef('ceiling_square');
const ceiling_triangle = ceilingDef('ceiling_triangle');

// Door leaf: hinged at the group origin; the hinge guest bonds to a doorway's door_host.
const door: BuildPieceDef = {
  id: 'door',
  footprintFamily: 'none',
  structuralRole: 'door',
  anchors: [
    { id: 'door_host', family: 'door_host', role: 'guest', localPosition: [0, 0, 0], localQuaternion: IDENTITY_Q, accepts: ['door_host'], maxSnapDistance: ANCHOR_MAX_SNAP, maxAngleDegrees: ANCHOR_MAX_ANGLE },
  ],
  occupancy: [
    { kind: 'door_opening', localKey: 'door', exclusiveWith: ['door_opening'] },
  ],
  support: { kind: 'parasite' },
  boundsLocal: box3(BUILD.doorwayWidth, 0, BUILD.doorwayHeight, BUILD.wallThick / 2),
  allowedRotationsDeg: [],
  rules: ['doorwayOnly'],
};

// Stairs: rest on the floor under the player, climb wallHeight over S (base at y=0). The stair
// fills its climb cell: one stair_volume slot per cell + height band, so a second staircase can't
// stack invisibly inside the first. Yaw snaps to the host floor's edge normals (R cycles edges).
const stairs: BuildPieceDef = {
  id: 'stairs',
  footprintFamily: 'none',
  structuralRole: 'stairs',
  anchors: [
    { id: 'floor_top', family: 'floor_top', role: 'guest', localPosition: [0, 0, 0], localQuaternion: IDENTITY_Q, accepts: ['floor_top'], maxSnapDistance: ANCHOR_MAX_SNAP, maxAngleDegrees: 180 },
  ],
  occupancy: [
    { kind: 'stair_volume', localKey: 'cell', exclusiveWith: ['stair_volume'] },
  ],
  support: { kind: 'parasite' },
  boundsLocal: box3(S / 2, 0, BUILD.wallHeight, S / 2),
  allowedRotationsDeg: [],
  rules: ['floorBelowPlayer'],
};

// Pillar: a slim wall-height column standing on a footprint CORNER, carrying load nearly straight
// down (SUPPORT.pillar). Its top counts as a wall-top level, so a ceiling may rest on it -- the
// intended cure for long cantilevered floors. Cross-section is build-local (not config BUILD).
export const PILLAR_HALF = 0.11; // half of the ~0.22m square cross-section
export const PILLAR_FLARE = 0.17; // half-width of the cap/base flare blocks

const pillar: BuildPieceDef = {
  id: 'pillar',
  footprintFamily: 'none',
  structuralRole: 'pillar',
  anchors: [
    // Base guest: bonds to the top face of the footprint it stands on (corner-snapped by the facade).
    { id: 'floor_top', family: 'floor_top', role: 'guest', localPosition: [0, -BUILD.wallHeight / 2, 0], localQuaternion: IDENTITY_Q, accepts: ['floor_top'], maxSnapDistance: ANCHOR_MAX_SNAP, maxAngleDegrees: ANCHOR_MAX_ANGLE },
    // Top hosts: a ceiling of either family may cap the pillar (same cap contract as a wall top).
    { id: 'wall_top_square', family: 'wall_top_square', role: 'host', localPosition: [0, BUILD.wallHeight / 2, 0], localQuaternion: IDENTITY_Q, accepts: ['wall_top_square'], maxSnapDistance: ANCHOR_MAX_SNAP, maxAngleDegrees: ANCHOR_MAX_ANGLE },
    { id: 'wall_top_triangle', family: 'wall_top_triangle', role: 'host', localPosition: [0, BUILD.wallHeight / 2, 0], localQuaternion: IDENTITY_Q, accepts: ['wall_top_triangle'], maxSnapDistance: ANCHOR_MAX_SNAP, maxAngleDegrees: ANCHOR_MAX_ANGLE },
  ],
  occupancy: [
    { kind: 'pillar_corner', localKey: 'corner', exclusiveWith: ['pillar_corner'] },
  ],
  support: { kind: 'pillar', loss: SUPPORT.pillar },
  boundsLocal: box3(PILLAR_HALF, -BUILD.wallHeight / 2, BUILD.wallHeight / 2, PILLAR_HALF),
  allowedRotationsDeg: [0], // rotation is meaningless on a square column: fixed
  rules: [],
};

export const CATALOG: Record<PieceId, BuildPieceDef> = {
  foundation_square,
  foundation_triangle,
  ceiling_square,
  ceiling_triangle,
  wall,
  half_wall,
  doorway,
  double_doorway,
  window: window_,
  door,
  stairs,
  pillar,
};

export function pieceDef(id: PieceId): BuildPieceDef {
  return CATALOG[id];
}

// --- piece-class predicates (mirror the building.ts helpers; kept local to build/) ---

export function isGroundFoundation(id: PieceId): boolean {
  return id === 'foundation_square' || id === 'foundation_triangle';
}
export function isCeiling(id: PieceId): boolean {
  return id === 'ceiling_square' || id === 'ceiling_triangle';
}
export function isFootprint(id: PieceId): boolean {
  return isGroundFoundation(id) || isCeiling(id);
}
export function isWallSlot(id: PieceId): boolean {
  return id === 'wall' || id === 'half_wall' || id === 'doorway' || id === 'double_doorway' || id === 'window';
}
export function footprintType(id: PieceId): 'square' | 'triangle' {
  return id === 'foundation_triangle' || id === 'ceiling_triangle' ? 'triangle' : 'square';
}
export function wallSlotHeight(id: PieceId): number {
  return id === 'half_wall' ? BUILD.wallHeight / 2 : BUILD.wallHeight;
}
